/** @format */

import {add} from 'date-fns';

import {formatDateToString} from 'helpers/dateHelpers';

import {DTO_DATE_FORMAT} from '../../constants/common';
import {
  GroupBreakdown,
  ScatterCollection,
  ScatterData,
  TooltipScatterData,
  YandexChangeGroup,
} from '../../models/scatters';

const addOneDay = (dateStr: string): string => {
  return formatDateToString(add(new Date(dateStr), {days: 1}), DTO_DATE_FORMAT);
};

const addGroups = (target: GroupBreakdown, src: GroupBreakdown): void => {
  (Object.keys(src) as YandexChangeGroup[]).forEach(group => {
    target[group] = (target[group] ?? 0) + (src[group] ?? 0);
  });
};

type SegmentDetail = {
  date: string;
  value: number;
};

type Segment = {
  endOn: string;
  totalEvents: number;
  details: SegmentDetail[];
  byGroup: GroupBreakdown;
};

type SegmentedGroup = {
  startIndex: number;
  endIndex: number;
};

interface Options {
  collection: ScatterCollection;
  rechartsDates: string[];
  daysRange: number;
  groupCollection?: {[date: string]: GroupBreakdown};
}

/**
 * Агрегирует данные скаттера с учетом факторов:
 * 1. Периодичность (дни, недели, месяцы).
 * 2. Схлопывание при недостатке места на графике.
 * @param collection
 * @param rechartsDates
 * @param daysRange
 */

const aggregateScatter = ({
  collection,
  rechartsDates,
  daysRange,
  groupCollection,
}: Options): ScatterData => {
  if (!rechartsDates.length) {
    return {};
  }

  const sortedRecharts = [...rechartsDates].sort();
  const sortedEntries = Object.entries(collection).sort(([d1], [d2]) => d1.localeCompare(d2));

  // 1. Разбиваем данные на "сегменты" по каждому значению из rechartsDates
  const segments: Segment[] = [];
  let logsIndex = 0;

  sortedRecharts.forEach(endOn => {
    let totalEvents = 0;
    const details: SegmentDetail[] = [];
    const byGroup: GroupBreakdown = {};

    while (logsIndex < sortedEntries.length && sortedEntries[logsIndex][0] <= endOn) {
      const [date, value] = sortedEntries[logsIndex];

      if (value !== 0) {
        totalEvents += value;
        details.push({date, value});

        if (groupCollection?.[date]) {
          addGroups(byGroup, groupCollection[date]);
        }
      }

      logsIndex += 1;
    }

    segments.push({endOn, totalEvents, details, byGroup});
  });

  const segmentsCount = segments.length;
  if (!segmentsCount) {
    return {};
  }

  // 2. Группируем сегменты с конца в зависимости от daysRange
  const groups: SegmentedGroup[] = [];

  if (daysRange < 1) {
    // Особый режим: всё схлопываем в одну кнопку на последней дате
    groups.push({startIndex: 0, endIndex: segmentsCount - 1});
  } else {
    const windowSize = Math.max(1, Math.floor(daysRange));

    let i = segmentsCount - 1;
    while (i >= 0) {
      const startIndex = Math.max(0, i - windowSize + 1);
      groups.push({startIndex, endIndex: i});
      i = startIndex - 1;
    }

    // Чтобы в результате ключи шли по возрастанию дат
    groups.reverse();
  }

  const result: ScatterData = {};

  // предыдущий endOn в терминах "кнопки" — для склейки периодов без разрывов
  let previousEndOn: string | null = null;

  groups.forEach(({startIndex, endIndex}) => {
    const endOn = sortedRecharts[endIndex];

    let totalEvents = 0;
    const mergedDetails: SegmentDetail[] = [];
    const mergedByGroup: GroupBreakdown = {};

    for (let i = startIndex; i <= endIndex; i += 1) {
      totalEvents += segments[i].totalEvents;
      mergedDetails.push(...segments[i].details);
      addGroups(mergedByGroup, segments[i].byGroup);
    }

    mergedDetails.sort((a, b) => a.date.localeCompare(b.date));

    // интервал для текущей кнопки:
    // первая начинается с самой первой даты на графике,
    // остальные — со следующего дня после предыдущего endOn
    const intervalStart = previousEndOn === null ? sortedRecharts[0] : addOneDay(previousEndOn);

    const tooltipData: TooltipScatterData = {
      endOn,
    };

    // startOn ставим и для нулевых значений,
    // если период больше одного дня
    if (intervalStart !== endOn) {
      tooltipData.startOn = intervalStart;
    }

    if (totalEvents > 0 && mergedDetails.length) {
      tooltipData.details = mergedDetails;
    }

    if (groupCollection && Object.keys(mergedByGroup).length) {
      tooltipData.byGroup = mergedByGroup;
    }

    result[endOn] = {
      value: totalEvents,
      tooltipData,
    };

    previousEndOn = endOn;
  });

  return result;
};

export {aggregateScatter};
